import React, { useState } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { StepButtonBar } from './StepButtonBar';

const PRIMARY = '#c4122f';

const buildConnectionsList = (
  requirement: any,
  companyUnitName: string | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string | undefined => {
  const splitToNames = (value?: string) =>
    (value ? value.split(',') : []).map((name) => name.trim()).filter(Boolean);
  const connections = [
    ...splitToNames(requirement?.processes),
    ...splitToNames(requirement?.aspects),
    ...splitToNames(requirement?.tags),
    ...(companyUnitName ? [companyUnitName] : []),
  ];

  if (connections.length === 0) return undefined;
  if (connections.length === 1) return connections[0];

  const and = formatMessage({ id: 'utils.connections.and' });
  return `${connections.slice(0, -1).join(', ')}${and}${connections[connections.length - 1]}`;
};

interface DeleteStep3PreviewProps {
  selectedRequirements: any[];
  selectedCompanyUnit: any;
  onBack: () => void;
  onCancel: () => void;
  onConfirmDelete: () => Promise<void>;
}

const DeleteStep3Preview: React.FC<DeleteStep3PreviewProps> = ({
  selectedRequirements,
  selectedCompanyUnit,
  onBack,
  onCancel,
  onConfirmDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDeleting, setIsDeleting] = useState(false);
  const { formatMessage } = useIntl();
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirmDelete();
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  };
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {isDeleting && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
          }}
        >
          <CircularProgress size={40} thickness={4} style={{ color: PRIMARY }} />
        </Box>
      )}
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            padding: isMobile ? theme.spacing(2) : theme.spacing(3),
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            minHeight: '400px',
          }}
        >
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              lineHeight: 1.4,
              fontSize: isMobile ? '16px' : '18px',
            }}
          >
             {formatMessage(
              { id: 'law-portal.delete-req.confirm-title' },
              { unit: selectedCompanyUnit?.name ?? '' }
            )}
          </Typography>

          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            {selectedRequirements.map((requirement: any) => {
              const connections = buildConnectionsList(
                requirement,
                selectedCompanyUnit?.name,
                formatMessage,
              );

              return (
                <Box
                  key={requirement?.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px' }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      backgroundColor: PRIMARY,
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                  </Box>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}
                  >
                    {connections
                      ? formatMessage(
                          { id: 'law-portal.delete-req.confirm-description-with-connections' },
                          { connections, requirement: requirement.name },
                        )
                      : formatMessage(
                          { id: 'law-portal.delete-req.confirm-description' },
                          { requirement: requirement.name },
                        )}
                  </Typography>
                </Box>
              );
            })}

            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                marginLeft: '67px',
                fontWeight: 500,
                color: '#222',
                fontSize: '14px',
              }}
            >
              {formatMessage({ id: 'law-portal.manage.requirements.table.delete.confirmation' })}
            </Typography>
          </Box>
        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleDelete}
          nextLabel="law-portal.delete-law.confirm-delete"
          nextDisabled={isDeleting}
          formatMessage={formatMessage}
          showBack={true}
          isDeleteStep={true}
        />
      </Box>
    </div>
  );
};

export default DeleteStep3Preview;
